import React from "react";
import { ShieldCheck } from "lucide-react";

/**
 * Gull-«V»-chip som vises ved siden av VIP-brukeres navn.
 * Bruk: <VipBadge user={user} />
 * Krever at user-objektet har `tier`, `tier_until`, `is_creator` felter ELLER `is_vip`-flagg.
 * Viser også en grønn ✓-checkmark hvis bruker er verifisert (verification_level >= 1).
 */
export default function VipBadge({ user, size = "sm", showTooltip = true, hideVerified = false }) {
  if (!user) return null;

  // Bestem VIP-status fra ulike kilder
  const isVip = !!user.is_vip || user.is_creator || (user.tier === "vip" || user.tier === "creator");
  const isVerified = (user.verification_level || 0) >= 1;

  if (!isVip && !isVerified) return null;

  const dim = size === "lg" ? "h-5 px-2 text-[11px]" : size === "md" ? "h-4 px-1.5 text-[10px]" : "h-3.5 px-1 text-[9px]";
  const icoSize = size === "lg" ? "h-4 w-4" : size === "md" ? "h-3.5 w-3.5" : "h-3 w-3";

  let vipTooltip = "VIP-medlem";
  if (user.is_creator) vipTooltip = "Verifisert skaper";
  else if (user.vip_started_at) {
    try {
      const d = new Date(user.vip_started_at);
      vipTooltip = `VIP-medlem siden ${d.toLocaleDateString("nb-NO", { month: "short", year: "numeric" })}`;
    } catch {}
  }
  const verifiedTooltip = user.verification_level === 2
    ? "Verifisert via kort (3DS/BankID)"
    : "Verifisert via Strex";

  return (
    <span className="inline-flex items-center gap-1 shrink-0">
      {isVip && (
        <span
          className={`inline-flex items-center justify-center ${dim} bg-[var(--gold)]/90 text-[var(--noir)] font-semibold uppercase tracking-wider rounded-sm`}
          title={showTooltip ? vipTooltip : undefined}
          data-testid="vip-badge"
        >
          V
        </span>
      )}
      {isVerified && !hideVerified && (
        <span
          className={`inline-flex items-center justify-center ${dim} bg-emerald-500/90 text-[var(--noir)] rounded-sm`}
          title={showTooltip ? verifiedTooltip : undefined}
          data-testid="verified-badge"
        >
          <ShieldCheck className={icoSize} />
        </span>
      )}
    </span>
  );
}
